import { Dictionary } from "@ngrx/entity"
import { createId, ExcludedKeys, jsonCopyObject } from "globals/helpers/helpers"
import { AllSameKeysEnforcer } from '../../../../src/app/helpers/helpers';
import { DropdownOption, hasProgramPermissions } from 'globals/store/programs.data';
import { PublicEditProfileStatusTypesType, QuoteStatus } from "globals/store/share.data";
import { GridCol } from "globals/helpers/grid-helpers";
import { JoiEvent, SnapshotInfo } from "globals/store/events.data";
import { JoiAccount } from "globals/store/accounts.data";
import { PermissionAccess } from "globals/store/permissions.data";
import { User } from "globals/store/user.data";
import { BrandingData } from "globals/store/publish.data";
import { JoiFile } from "globals/store/files.model";

export interface SaveBudgetCellData<R> { saveData: any, column: GridCol, row: R }
export interface AddItemRowData<R> { event: MouseEvent, row: R }

export type HomeBudgetSubpage = 'summary' | 'quotes' | 'table' | 'line-items' | 'share'
export const HomeBudgetSubpages: HomeBudgetSubpage[] = ['summary', 'table', 'line-items'] // , 'share', 'quotes'

export type LineItemStatus = 'Estimate' | 'Quote' | 'Actual'
export interface LineItem {
  id: string
  itemId?: string // equivalent to isItem boolean but allows future tracking
  item: string
  description: string
  quantity: number
  unitCost: number // unit cost in costs, unit rate in revenue

  unitRate?: number // overwritable in costs for link to revenue (but only when markup not enabled - when unit rate enabled and markup toggled off from on and had applied an item, this reverts to item rate)
  itemRate?: number

  recurrence: number
  status?: LineItemStatus 
  tax?: boolean
  discount?: boolean
  markup?: boolean // mark up applies to this 
  fees?: boolean
  show?: boolean // really only for manage budget tasks / assets
  total: number
}
export interface SectionSettings {
  useTax: boolean
  taxRate: number
  conversionRate: number
  taxInclusive?: boolean
  useDiscount: boolean
  discountRate: number
  useFees: boolean
  feeRate: number
  
  useCurrency?: boolean
  currency: string
  

  // cost sections only
  useMarkup?: boolean
  markupRate?: number
  useUnitRate?: boolean
}

export type BudgetSummaryTaxType = 'inclusive' | 'exclusive' | 'both'

export interface BudgetSection {
  name: string
  id: string
  lineItems: LineItem[]
  settings: SectionSettings
  discount: number
  fees: number
  tax: number
  subTotal: number
  total: number
  schedule?: BudgetSchedule
  files?: JoiFile[]

  isQuote?: boolean
  linkId?: string
  linkedCache?: BudgetSection // in order to properly compare previous with new versions need to keep an entire copy of the previous budget section
  linkJustDeleted?: boolean
  isTasks?: boolean
  isAssets?: boolean

}

export interface BudgetQuoteSection extends BudgetSection {
  resourceId: string
  resourceName: string
  quoteRef?: string
  quoteType?: string
  quoteLink?: string
  lastSubmitted?: number // only through public link
  submissionDeadline?: number
  dialogName?: string // for selecting quote only
  notes?: string // user can include password if they want (not a submission password as previously thought)
}

export const BudgetQuoteSectionProps: ExcludedKeys<BudgetQuoteSection, BudgetSection> = ['quoteRef', 'quoteType', 'quoteLink', 'lastSubmitted', 'submissionDeadline', 'notes', 'dialogName']

export interface BudgetQuoteShareSection extends BudgetQuoteSection { // for share only
  budgetCurrency: string
  eventName: string 
  eventDateString: string
  brandingData: BrandingData
}

export const BudgetQuoteShareSectionProps: ExcludedKeys<BudgetQuoteShareSection,BudgetQuoteSection> = ['budgetCurrency', 'eventDateString', 'eventName']

export interface UpdateQuoteData {
  password?: string
  quoteId?: string
  updatedQuote: BudgetQuoteSection
}

export interface PublicQuoteInfo { // for use in posting to create-edit-profile-code only
  resourceId: string
  eventId: string
  accountId: string
  quoteId: string
  sectionName?: string
  referenceNo?: string
  quoteStatus: QuoteStatus
  status: PublicEditProfileStatusTypesType
  editLinkInBrief?: boolean
}


export type BudgetScheduleItemStatus = 'Pending' | 'Paid'
export interface BudgetScheduleItem {
  id: string
  date: number
  description: string
  amount: number
  status: BudgetScheduleItemStatus
  costSectionId: string
}

export interface BudgetSchedule {
  pending: number
  paid: number
  total: number
  items: BudgetScheduleItem[]
}

export interface OtherRevenueItem {
  id: string
  amount: number
  description: string
}

export interface BudgetRevenueShareBase {
  revenueIds: string[]
  sections: Dictionary<BudgetSection>
  revenuePaymentSchedule?: BudgetSchedule
  revenueFiles?: JoiFile[]
  version: number
  currency: string
  totalRevenue: number
  revenueDiscount?: number
  revenueFees?: number
  revenueSubtotal?: number
  revenueTax?: number
}

export interface Budget extends BudgetRevenueShareBase {
  costIds: string[]
  eventId: string
  accountId: string
  revenueSnapshots: SnapshotInfo[]
  costSnapshots: SnapshotInfo[]
  summarySnapshots: SnapshotInfo[]
  otherRevenues: OtherRevenueItem[]
  attendees: number
  totalCosts: number
  quotes: Dictionary<BudgetQuoteSection>
  costsDiscount?: number
  costsFees?: number
  costsSubtotal?: number
  costsTax?: number
  summaryTaxInclusive?: boolean 
  workforceEmails?: string[]
}


export interface BudgetRevenueShare extends BudgetRevenueShareBase {
  headerDesktopUrl?: string
  headerMobileUrl?: string
  footerText?: string
  eventName?: string
  dateString: string
}



export interface BudgetPie {
  quoted: number
  estimate: number
  actual: number
  other: number
  total: number
}

export const pieOpacities: AllSameKeysEnforcer<BudgetPie> = { other: 0.4, quoted: 0.6, estimate: 0.8, actual: 1, total: 0 }
export const pieLabels: AllSameKeysEnforcer<BudgetPie> = { other: 'Other', quoted: 'Quoted', estimate: 'Estimate', actual: 'Actual', total: 'Total' }
export const revenueSliceColors: AllSameKeysEnforcer<BudgetPie> = { other: '#4FB5B2', quoted: '#149C99', estimate: '#89CDCC', actual: '#C4E6E5', total: null }
export const costSliceColors: AllSameKeysEnforcer<BudgetPie> = { other: '#F47F74', quoted: '#F05454', estimate: '#F7A9A9', actual: '#FBD4D4', total: null }


export const pieKeys: (keyof BudgetPie)[] = ['quoted', 'estimate', 'actual', 'other']
export interface ArcData {
  color: string
  startPercent: number
  endPercent: number
  label: string
}


export interface CashFlowItem {
  date: number
  description: string
  received?: number
  billed?: number
  status: BudgetScheduleItemStatus
  balance: number
  costSectionId: string
}

export interface BudgetSummary {
  revenueSubtotal: number
  totalRevenue: number
  totalCosts: number
  profit: number
  profitMargin: number
  revenueArcs: ArcData[]
  costsArcs: ArcData[]
  cashFlow: CashFlowItem[]
}

export interface AccountBudgetMonthSummary {
  completedRevenue: number
  confirmedRevenue: number
  pendingRevenue: number
  totalRevenue: number
  completedCosts: number
  confirmedCosts: number
  pendingCosts: number
  totalCosts: number
  profit: number
  profitMargin: number
  // cash flow
  revenuePaid: number
  revenuePending: number
  billsPaid: number
  billsPending: number
  cashFlow: number

  budgetsUsed: number
}

export const createEmptyAccountBudgetMonthlySummary = (): AccountBudgetMonthSummary => ({
  completedRevenue: 0,
  confirmedRevenue: 0,
  pendingRevenue: 0,
  totalRevenue: 0,
  completedCosts: 0,
  confirmedCosts: 0,
  pendingCosts: 0,
  totalCosts: 0,
  profit: 0,
  profitMargin: 0,
  revenuePaid: 0,
  revenuePending: 0,
  billsPaid: 0,
  billsPending: 0,
  cashFlow: 0,
  budgetsUsed: 0
})


export interface SimpleGridRow<T> {
  title: string
  key: keyof T
}

export interface SimpleGridCol {
  title: string
  key: string | number
}


export const createEmptyBudgetSchedule = (): BudgetSchedule => ({
  total: 0,
  pending: 0,
  paid: 0,
  items: []
})

export const createDefaultLineItem = (id?: string):LineItem => ({
  id: id || createId(),
  item: '',
  description: '',
  quantity: 0,
  unitCost: 0,
  recurrence: 0,
  status: 'Estimate',
  total: 0
})

export const createEmptySection = (account: Partial<JoiAccount>, addSchedule: boolean = false, id = createId(), nEmptyItems = 1) => {
  const section: BudgetSection = {
    name: '',
    id,
    lineItems: Array.from({ length: nEmptyItems }).map(createDefaultLineItem),
    settings: {
      useTax: false,
      taxRate: account.taxRate || .10,
      taxInclusive: account.taxInclusive || false,
      useDiscount: false,
      discountRate: account.discountRate || .10,
      useFees: false,
      feeRate: account.feeRate || .10,
      currency: null,
      conversionRate: 1
    },
    fees: 0,
    tax: 0,
    subTotal: 0,
    total: 0,
    discount: 0,
  }
  if (addSchedule) section.settings.markupRate = account.markupRate || 0.1
  if (addSchedule) section.schedule = createEmptyBudgetSchedule()
  return section
}


export const createDefaultBudget = (eventId: string, account: JoiAccount): Budget => {
  const budget = {
    version: 1,
    eventId,
    accountId: account.id,
    costIds: ['defaultCosts'],
    revenueIds: ['defaultRevenue'],
    revenuePaymentSchedule: createEmptyBudgetSchedule(),
    sections: { 
      defaultRevenue: createEmptySection(account, false, 'defaultRevenue'),
      defaultCosts: createEmptySection(account, true, 'defaultCosts')
    },
    revenueSnapshots: [],
    costSnapshots: [],
    summarySnapshots: [],
    currency: 'AUD',
    otherRevenues: [{ id: createId(), amount: null, description: '' }],
    attendees: 100,
    totalRevenue: 0,
    totalCosts: 0,
    summaryTaxInclusive: false,
    workforceEmails: [],
    quotes: {}
  }
  console.log({ account, budget })
  return budget
}

// { id '', title: '', type: }

export type ManageBudgetLinkOption =  'remove' | 'unlink' | 'manage'
export const manageBudgetLinkDropdownOptions: DropdownOption<ManageBudgetLinkOption>[] = [
  { id: 'remove', title: 'Remove linked section' },
  { id: 'unlink', title: 'Unlink and edit' }
]

export const manageQuoteLinkDropdownOptions: DropdownOption<ManageBudgetLinkOption>[] = jsonCopyObject(manageBudgetLinkDropdownOptions)
manageQuoteLinkDropdownOptions.push({ id: 'manage', title: 'Manage Quote' })

export const manageBudgetTasksDropdownOptions = jsonCopyObject(manageQuoteLinkDropdownOptions)
manageBudgetTasksDropdownOptions.find(o => o.id === 'manage').title = 'Manage Budget Tasks'

export const manageBudgetAssetsDropdownOptions = jsonCopyObject(manageQuoteLinkDropdownOptions)
manageBudgetAssetsDropdownOptions.find(o => o.id === 'manage').title = 'Manage Budget Assets'

export const canViewBudget = (event: JoiEvent, account: Partial<JoiAccount>, user: User, hasProgPermissions: boolean) => {

  const explicitlyOnBudget =event.budgetWorkforceEmails.includes(user.email) && hasProgPermissions
  const isManager = (account.admin?.includes(user.uid) || account.executive?.includes(user.uid))
  const adminOnEventAndWorkforce = isManager && !!event?.workforce.find(w => w.uid === user.uid)
  const isEventOwner = event.owner_uid === user.uid

  return adminOnEventAndWorkforce || isEventOwner || explicitlyOnBudget
}

export const canViewBudgetCheckProgramPermissions = (event: JoiEvent, account: Partial<JoiAccount>, user: User, permissions: PermissionAccess) => canViewBudget(event, account, user, hasProgramPermissions(event, user.uid, permissions))


